import { translateTemplateText, useLiquidGlassPress } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@tma.js/sdk';
import { type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { ListCategoryWithExternalDescription } from '../../client/types';
import { getColor } from '../../helpers/color';
import { trackHealthLeeCrossSellShown } from '../../helpers/healthLee';
import { CategoryIcon } from '../common/CategoryIcon';
import { CommonIcon } from '../common/CommonIcon';
import { healthLeeAchievementTemplateCategoryId } from '../achievements/healthLeeAchievements';

const ROW_DIVIDER_BORDER = '1px solid color-mix(in srgb, var(--tg-theme-text-color) 16%, transparent)';

type CategoryListItemProps = {
    category: ListCategoryWithExternalDescription;
    childrenMap: Record<string, ListCategoryWithExternalDescription[]>;
    expandedMap: Record<string, boolean>;
    isSubCategory?: boolean;
    showDivider?: boolean;
    onCategoryClick: () => void;
    onToggleExpand: (categoryId: string) => void;
};

export const CategoryListItem: FC<CategoryListItemProps> = ({
    category,
    childrenMap,
    expandedMap,
    isSubCategory = false,
    showDivider = false,
    onCategoryClick,
    onToggleExpand,
}) => {
    const { t } = useTranslation();
    const { icon, iconColor, name, id, externalLink, externalDescription } = category;
    const displayName = translateTemplateText(t, category.templateId, 'name', name);
    const displayExternalDescription = translateTemplateText(t, category.templateId, 'description', externalDescription ?? '');
    const subCategories = childrenMap[id] || [];
    const hasSubCategories = subCategories.length > 0 && !isSubCategory;

    const ownRemaining = (category.questProgressTotal || 0) - (category.questProgressCompleted || 0);
    const subsRemaining = !isSubCategory
        ? subCategories.reduce((sum, sub) => sum + ((sub.questProgressTotal || 0) - (sub.questProgressCompleted || 0)), 0)
        : 0;
    const totalRemaining = ownRemaining + subsRemaining;
    const isExternalCategory = !!externalLink;
    const expanded = !!expandedMap[id];

    useEffect(() => {
        if (category.templateId === healthLeeAchievementTemplateCategoryId || category.templateId?.includes('healthlee')) {
            trackHealthLeeCrossSellShown('category_list');
        }
    }, [category.templateId]);

    const { pressed, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClick } = useLiquidGlassPress({
        preventDefault: true,
        onActivate: () => {
            hapticFeedback.impactOccurred.ifAvailable('light');
            onCategoryClick();
        },
    });

    return (
        <Box
            component="button"
            type="button"
            aria-label={isExternalCategory ? displayExternalDescription : displayName}
            data-external-category={isExternalCategory ? true : undefined}
            data-template-category={category.templateId ?? undefined}
            onClick={onClick}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            sx={{
                display: 'block',
                width: '100%',
                m: 0,
                p: 0,
                border: 'none',
                borderTop: showDivider ? ROW_DIVIDER_BORDER : 'none',
                background: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                font: 'inherit',
                color: 'inherit',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none',
                bgcolor: pressed ? 'color-mix(in srgb, var(--tg-theme-text-color) 6%, transparent)' : 'transparent',
                transition: 'background-color 0.12s ease',
            }}
        >
            <Box display="flex" alignItems="center" gap={2} sx={{ minWidth: 0 }}>
                <Box paddingLeft={isSubCategory ? 3 : 2} py={1.5}>
                    <Box
                        sx={{
                            width: isSubCategory ? 32 : 40,
                            height: isSubCategory ? 32 : 40,
                            borderRadius: isSubCategory ? 1 : 2,
                            background: getColor(iconColor),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <CategoryIcon
                            icon={icon}
                            iconColor={iconColor}
                            customIcon={category.customIcon}
                            type="filled"
                            color="white"
                            size={isSubCategory ? 20 : 28}
                        />
                    </Box>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }} py={1.5}>
                    <Text
                        weight={isSubCategory ? '1' : '2'}
                        style={{ color: 'var(--tg-theme-text-color)', fontSize: isSubCategory ? '13px' : '14px' }}
                    >
                        {displayName}
                    </Text>
                    {!isSubCategory && (
                        <Box mt={0.3}>
                            <Text style={{ fontSize: '12px', color: 'var(--tgui--hint_color)' }}>
                                {displayExternalDescription ||
                                    (hasSubCategories
                                        ? t('categoryCounts.subcategoriesAndQuests', { categories: subCategories.length, quests: totalRemaining })
                                        : t('quest.count', { count: totalRemaining }))}
                            </Text>
                        </Box>
                    )}
                </Box>
                {isExternalCategory ? (
                    <Box paddingRight={2}>
                        <CommonIcon icon="launch" type="filled" size={22} color="var(--tgui--hint_color)" />
                    </Box>
                ) : hasSubCategories ? (
                    <Stack
                        sx={{ alignSelf: 'stretch', width: '15%', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                        onClick={e => {
                            e.stopPropagation();
                            hapticFeedback.impactOccurred.ifAvailable('light');
                            onToggleExpand(id);
                        }}
                    >
                        <CommonIcon
                            icon={expanded ? 'expand_less' : 'expand_more'}
                            type="filled"
                            size={26}
                            color="var(--tgui--hint_color)"
                        />
                    </Stack>
                ) : isSubCategory ? (
                    <Box paddingRight={2}>
                        <Text style={{ fontSize: '12px', color: 'var(--tgui--hint_color)' }}>{t('quest.count', { count: totalRemaining })}</Text>
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
};
